import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import { ReportCategory, ReportDefinition } from '@drivingdoc/shared-types/reports';
import { listReportsOptions } from '@drivingdoc/api-client/generated/react-query';
import { GenerateReportDialog } from 'components/Dialogs/Reports';
import PageContainer from 'components/PageContainer';

import { ReportDefinitionWithIcon } from 'types/reports';

import ReportCategorySection from './components/ReportCategorySection';
import ReportCategorySectionSkeleton from './components/ReportCategorySectionSkeleton';
import { resolveIcon } from './utils/iconResolver';

/**
 * Category metadata for display
 */
const REPORT_CATEGORIES: Record<ReportCategory, { label: string }> = {
  student: { label: 'Student Reports' },
  financial: { label: 'Financial Reports' },
  scheduling: { label: 'Scheduling Reports' },
  compliance: { label: 'Compliance Reports' },
  operational: { label: 'Operational Reports' },
};

/**
 * Category order for display
 */
const CATEGORY_ORDER: ReportCategory[] = ['student', 'financial', 'scheduling', 'compliance', 'operational'];

/**
 * Transform API reports to include resolved icons
 */
function transformReports(reports: ReportDefinition[]): ReportDefinitionWithIcon[] {
  return reports.map((report) => ({
    ...report,
    icon: resolveIcon(report.iconName),
  }));
}

/**
 * Get reports filtered by category
 */
function getReportsByCategory(
  reports: ReportDefinitionWithIcon[],
  category: ReportCategory
): ReportDefinitionWithIcon[] {
  return reports.filter((report) => report.category === category);
}

/**
 * Get unique categories from reports, sorted by predefined order
 */
function getUniqueCategories(reports: ReportDefinitionWithIcon[]): ReportCategory[] {
  const categories = new Set(reports.map((r) => r.category));
  return CATEGORY_ORDER.filter((category) => categories.has(category));
}

export function Reports() {
  const dialogs = useDialogs();
  const { data, isLoading, isError, error } = useQuery(listReportsOptions());

  const handleGenerateReport = (report: ReportDefinitionWithIcon) => {
    dialogs.open(GenerateReportDialog, { report });
  };

  if (isLoading) {
    return (
      <PageContainer title="Reports">
        <Stack spacing={4}>
          <ReportCategorySectionSkeleton cardCount={2} />
          <ReportCategorySectionSkeleton cardCount={2} />
          <ReportCategorySectionSkeleton cardCount={2} />
        </Stack>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer title="Reports">
        <Alert severity="error">Failed to load reports: {error?.message ?? 'Unknown error'}</Alert>
      </PageContainer>
    );
  }

  const reports = transformReports(((data as { reports: ReportDefinition[] } | undefined)?.reports ?? []));
  const categories = getUniqueCategories(reports);

  return (
    <PageContainer title="Reports">
      <Stack spacing={4}>
        {categories.map((category) => {
          const categoryReports = getReportsByCategory(reports, category);
          if (categoryReports.length === 0) return null;

          return (
            <ReportCategorySection
              key={category}
              title={REPORT_CATEGORIES[category].label}
              reports={categoryReports}
              onGenerateReport={handleGenerateReport}
            />
          );
        })}
      </Stack>
    </PageContainer>
  );
}

export default Reports;
